import React from 'react';
import { GroupAddons } from './Addons/Group';
import { GroupAddonsLegacy } from './Addons/GroupLegacy';
import { ServiceData, SlotService } from '../../../../utils/state/types';
import { mapToArray } from '../../../../utils';
import { useFormActions } from '../../Hooks/useFormActions';
import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';
import { PaymentType } from '../../../../types/types';
import {
  Text,
  SectionNotification,
  SectionNotificationType,
} from 'wix-ui-tpa/cssVars';
import { classes, st } from './AddonsDetails.st.css';
import {
  useTranslation,
  useEnvironment,
  useExperiments,
} from '@wix/yoshi-flow-editor';
import { ExperimentsConsts } from '../../../../consts/experiments';

export const AddonsDetails = ({
  serviceData,
  participantIndex,
}: {
  serviceData: ServiceData;
  participantIndex?: number;
}) => {
  const { onAddonSelect, onAddonQuantityChange } = useFormActions();
  const singleService = mapToArray<SlotService>(serviceData.slotServices)[0];
  const {
    addOnByGroup,
    selectedAddons = {},
    service,
    selectedPaymentOption,
    participantAddons,
  } = singleService;
  const activeSelectedAddons =
    participantIndex === undefined
      ? selectedAddons
      : participantAddons?.[participantIndex] ?? {};
  const serviceSlotId = singleService.nestedSlot.lineItemId;
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const isAddOnsWithImagesEnabled = experiments.enabled(
    ExperimentsConsts.AddOnsWithImages,
  );

  const shouldShowAddons = (
    paymentType: string,
    hasActivePlan: boolean,
  ): boolean => {
    if (paymentType !== PaymentType.BUY_PLAN) {
      return true;
    }
    return hasActivePlan;
  };

  if (
    service.type === ServiceType.APPOINTMENT ||
    selectedPaymentOption?.type === PaymentType.SUBSCRIPTION ||
    !addOnByGroup ||
    addOnByGroup?.length === 0
  ) {
    return null;
  }

  const showAddons = shouldShowAddons(
    selectedPaymentOption?.type || '',
    !!selectedPaymentOption?.creditRemain,
  );

  return (
    <div
      className={st(classes.addon_group, {
        // .title/.groupName/.groupSubTitle/.groupLimitation are shared with
        // GroupAddonsLegacy — this state must not fire on the off arm.
        isMobile: isMobile && isAddOnsWithImagesEnabled,
      })}
    >
      <Text className={classes.title}>
        {t('app.bookings-form.add-ons.section.title')}
      </Text>
      {!showAddons ? (
        <div className={classes.notification}>
          <SectionNotification type={SectionNotificationType.wired}>
            <SectionNotification.Text>
              {t('app.bookings-form.add-ons.select-plan-message')}
            </SectionNotification.Text>
          </SectionNotification>
        </div>
      ) : (
        addOnByGroup
          ?.filter((group) => group.addOns?.length)
          .map((group) => {
            const onSelect = (addonId: string, isSelected: boolean) =>
              onAddonSelect(
                addonId,
                isSelected,
                serviceSlotId,
                participantIndex,
              );
            const onQuantityChange = (addonId: string, quantity: number) =>
              onAddonQuantityChange(
                addonId,
                quantity,
                serviceSlotId,
                participantIndex,
              );

            return isAddOnsWithImagesEnabled ? (
              <GroupAddons
                key={group.id}
                groupId={group.id!}
                name={group.name!}
                addOns={group.addOns!}
                maxNumberOfAddOns={group.maxNumberOfAddOns ?? undefined}
                prompt={group.prompt ?? undefined}
                selectedAddons={activeSelectedAddons}
                onAddonSelect={onSelect}
                onAddonQuantityChange={onQuantityChange}
              />
            ) : (
              <GroupAddonsLegacy
                key={group.id}
                groupId={group.id!}
                name={group.name!}
                addOns={group.addOns!}
                maxQuantity={group.maxNumberOfAddOns ?? undefined}
                prompt={group.prompt ?? undefined}
                selectedAddons={activeSelectedAddons}
                onAddonSelect={onSelect}
                onAddonQuantityChange={onQuantityChange}
              />
            );
          })
      )}
    </div>
  );
};
